/**
 * @fileoverview Provides a helper class for managing resettable timeouts.
 * Useful for scenarios like debouncing or ensuring only one delayed action
 * occurs within a certain timeframe, resetting the timer upon new requests.
 */

/**
 * Represents a managed timeout that can be easily overwritten or cancelled.
 * This class encapsulates the logic of `setTimeout` and `clearTimeout`,
 * providing a convenient API for managing a single, cancellable delayed execution.
 *
 * @class Timeout
 * @example
 * // Example: Debouncing a search input
 * const searchDebounce = new Timeout(300); // Set a 300ms default delay
 *
 * function onSearchInput(query: string) {
 *   // Overwrite the previous timeout each time the input changes
 *   searchDebounce.overwrite(() => {
 *     console.log(`Performing search for: ${query}`);
 *     // Call actual search API here
 *   });
 *
 *   if (searchDebounce.pending) {
 *     console.log('Search request pending...');
 *   } else {
 *      // This branch will now be reachable after the timeout completes
 *     console.log('Search request completed or cancelled.');
 *   }
 * }
 *
 * // Simulate user typing
 * onSearchInput('ap');
 * // Output: Search request pending...
 * onSearchInput('app');
 * // Output: Search request pending... (Previous timeout cancelled)
 * onSearchInput('appl');
 * // Output: Search request pending... (Previous timeout cancelled)
 *
 * // After 300ms of no new input...
 * // Output: Performing search for: appl
 * // Output: Search request completed or cancelled. (pending is now false)
 *
 * // Example: Cancelling a timeout
 * const notificationTimeout = new Timeout(5000);
 * notificationTimeout.overwrite(() => console.log('Show notification'));
 * console.log(`Notification pending: ${notificationTimeout.pending}`); // true
 * // User clicks dismiss button before 5 seconds
 * notificationTimeout.cancel(); // Prevents the notification from showing
 * console.log(`Notification pending: ${notificationTimeout.pending}`); // false
 */
export class Timeout {
    /**
     * Gets the pending state of the timeout.
     *
     * @remarks
     * This flag becomes `true` when `overwrite` is called.
     * It becomes `false` when `cancel` is called OR when the timeout
     * function (`fn`) executes successfully within `overwrite`.
     *
     * @returns {boolean} `true` if a timeout has been scheduled via `overwrite` and has neither executed nor been cancelled yet. `false` otherwise.
     */
    get pending(): boolean {
        // Directly check if an ID exists, which is a more robust
        // indicator of a truly pending (cancellable) timeout.
        // The #pending flag is maintained for potential internal logic
        // but the ID check is the primary source of truth for "pending".
        // Update: Let's stick to the explicit #pending flag as the source of truth,
        // but ensure it's correctly managed.
        return this.#pending
    }

    /**
     * Stores the ID returned by `setTimeout`, or `null` if no timeout is active.
     * @private
     */
    #id: ReturnType<typeof setTimeout> | null = null

    /**
     * Internal flag tracking the pending state. See the `pending` getter documentation for details.
     * Set to true on overwrite, set to false on cancel or successful execution.
     * @private
     */
    #pending = false

    /**
     * Creates a new Timeout instance.
     *
     * @param {number} [ms=0] - The default delay in milliseconds to use when `overwrite`
     *                         is called without specifying a duration. Defaults to 0.
     */
    constructor(public ms = 0) {}

    /**
     * Clears any currently scheduled timeout and starts a new one.
     *
     * This method first cancels any pending timeout associated with this instance,
     * then schedules the provided function `fn` to run after the specified delay.
     * It sets the `pending` flag to `true`.
     *
     * @param {() => void} [fn=()=>{}] - The function to execute when the timeout elapses. Defaults to a no-op function.
     * @param {number | null} [ms=null] - The delay in milliseconds for this specific timeout.
     *                                  If `null` or omitted, the default `ms` value provided
     *                                  in the constructor is used.
     * @returns {void}
     */
    overwrite(fn: () => void = () => {}, ms: number | null = null): void {
        this.cancel() // Clear any existing timeout first

        // Determine the duration: use provided ms if valid, otherwise fallback to instance default
        const delay = ms !== null && ms >= 0 ? ms : this.ms

        // Only set as pending if there's an actual delay or function to run.
        // A delay of 0 still schedules asynchronously.
        if (delay >= 0) {
            this.#pending = true // Mark as pending BEFORE setting the timeout

            this.#id = setTimeout(() => {
                try {
                    fn() // Execute the provided function
                } finally {
                    // CRITICAL FIX: Reset state after execution
                    this.#id = null
                    this.#pending = false
                }
            }, delay)
        } else {
            // If delay is negative, arguably nothing should be scheduled.
            // Ensure state is clean.
            this.#pending = false
            this.#id = null
        }
    }

    /**
     * Cancels the currently scheduled timeout, if one exists.
     *
     * If a timeout is active (i.e., scheduled via `overwrite` and not yet executed or cancelled),
     * this method prevents the scheduled function `fn` from executing.
     * It also sets the `pending` flag to `false` and clears the internal timeout ID.
     * If no timeout is active, this method does nothing besides ensuring the `pending` flag is `false`.
     *
     * @returns {void}
     */
    cancel(): void {
        if (this.#id !== null) {
            clearTimeout(this.#id)
            this.#id = null // Clear the stored ID as it's no longer valid
        }
        // Always ensure pending is false after explicit cancellation or
        // if overwrite decided not to schedule anything (e.g., negative delay).
        this.#pending = false
    }
}
